import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../slices/authSlice';
import { useTheme } from '../ThemeContext';
import {
  HiOutlineHome, HiOutlineLightningBolt, HiOutlineClipboardList,
  HiOutlineCreditCard, HiOutlineCog, HiOutlineLogout, HiOutlineMoon,
  HiOutlineSun, HiOutlineMenu, HiOutlineX, HiOutlineCurrencyDollar,
  HiOutlineChevronRight,
} from 'react-icons/hi';

const navItems = [
  { to: '/', label: 'Dashboard', icon: HiOutlineHome },
  { to: '/scenarios', label: 'Scenarios', icon: HiOutlineLightningBolt },
  { to: '/executions', label: 'Executions', icon: HiOutlineClipboardList },
  { to: '/billing', label: 'Billing', icon: HiOutlineCreditCard },
  { to: '/credits', label: 'Credits', icon: HiOutlineCurrencyDollar },
  { to: '/settings', label: 'Settings', icon: HiOutlineCog },
];

export default function Layout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const user = useSelector((s) => s.auth.user);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const planColors = {
    free: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    starter: 'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300',
    pro: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-950">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 flex flex-col
        bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800
        transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-purple-600 rounded-lg flex items-center justify-center shadow-glow">
              <HiOutlineLightningBolt className="text-white" size={16} />
            </div>
            <span className="text-lg font-bold tracking-tight">Ads Connect</span>
          </div>
          <button className="lg:hidden text-gray-400 hover:text-gray-600 transition-colors" onClick={() => setSidebarOpen(false)}>
            <HiOutlineX size={20} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-brand-600 text-white shadow-glow'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="flex items-center gap-3">
                    <Icon size={18} className={isActive ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'} />
                    {label}
                  </span>
                  {isActive && <HiOutlineChevronRight size={14} className="opacity-60" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-gray-100 dark:border-gray-800 space-y-1">
          {/* User info */}
          <div className="px-3 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                {user?.email?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">{user?.email}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md ${planColors[user?.plan] || planColors.free}`}>
                    {user?.plan}
                  </span>
                  <span className="text-[10px] text-gray-400">{user?.credits?.toLocaleString()} cr</span>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={toggle}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            {dark ? <HiOutlineSun size={17} /> : <HiOutlineMoon size={17} />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          >
            <HiOutlineLogout size={17} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <header className="h-16 flex items-center justify-between px-5 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 lg:hidden flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            <HiOutlineMenu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-gradient-to-br from-brand-500 to-purple-600 rounded-lg flex items-center justify-center">
              <HiOutlineLightningBolt className="text-white" size={14} />
            </div>
            <span className="font-bold">Ads Connect</span>
          </div>
          <button onClick={toggle} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            {dark ? <HiOutlineSun size={20} /> : <HiOutlineMoon size={20} />}
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 lg:p-8 max-w-7xl mx-auto w-full">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}


// const navItems = [
//   { to: '/', label: 'Dashboard', icon: HiOutlineHome },
//   { to: '/scenarios', label: 'Scenarios', icon: HiOutlineLightningBolt },
//   { to: '/executions', label: 'Executions', icon: HiOutlineClipboardList },
//   { to: '/billing', label: 'Billing', icon: HiOutlineCreditCard },
//   { to: '/credits', label: 'Credits', icon: HiOutlineCurrencyDollar },
//   { to: '/settings', label: 'Settings', icon: HiOutlineCog },
// ];

// export default function Layout() {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { dark, toggle } = useTheme();
//   const user = useSelector((s) => s.auth.user);
//   const [sidebarOpen, setSidebarOpen] = useState(false);

//   const handleLogout = () => {
//     dispatch(logout());
//     navigate('/login');
//   };

//   const linkClass = ({ isActive }) =>
//     `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
//       isActive
//         ? 'bg-brand-600 text-white'
//         : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
//     }`;

//   return (
//     <div className="flex h-screen overflow-hidden">
//       {/* Mobile overlay */}
//       {sidebarOpen && (
//         <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
//       )}

//       {/* Sidebar */}
//       <aside className={`
//         fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-900 border-r border-gray-200
//         dark:border-gray-800 flex flex-col transition-transform lg:translate-x-0
//         ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
//       `}>
//         <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-800">
//           <span className="text-xl font-bold text-brand-600">AdsSync</span>
//           <button className="lg:hidden text-gray-500" onClick={() => setSidebarOpen(false)}>
//             <HiOutlineX size={20} />
//           </button>
//         </div>

//         <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
//           {navItems.map(({ to, label, icon: Icon }) => (
//             <NavLink key={to} to={to} end={to === '/'} className={linkClass} onClick={() => setSidebarOpen(false)}>
//               <Icon size={20} />
//               {label}
//             </NavLink>
//           ))}
//         </nav>

//         <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-2">
//           <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 truncate">
//             {user?.email}
//           </div>
//           <div className="flex items-center gap-2 px-4 text-xs text-gray-500 dark:text-gray-400">
//             <span className="inline-block px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-700/30 text-brand-700 dark:text-brand-300 font-semibold uppercase">
//               {user?.plan}
//             </span>
//             <span>{user?.credits?.toLocaleString()} credits</span>
//           </div>
//           <button onClick={toggle} className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
//             {dark ? <HiOutlineSun size={20} /> : <HiOutlineMoon size={20} />}
//             {dark ? 'Light Mode' : 'Dark Mode'}
//           </button>
//           <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
//             <HiOutlineLogout size={20} />
//             Logout
//           </button>
//         </div>
//       </aside>

//       {/* Main content */}
//       <main className="flex-1 flex flex-col overflow-hidden">
//         <header className="h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 lg:hidden">
//           <button onClick={() => setSidebarOpen(true)} className="text-gray-600 dark:text-gray-400">
//             <HiOutlineMenu size={24} />
//           </button>
//           <span className="text-lg font-bold text-brand-600">AdsSync</span>
//           <div className="w-6" />
//         </header>
//         <div className="flex-1 overflow-y-auto p-6">
//           <Outlet />
//         </div>
//       </main>
//     </div>
//   );
// }
